import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { PUBLISHED_WEB_VERSION } from './published-version.ts';

// Tagged APK builds receive this value directly from the Git tag. The fallback
// is the latest version published by the same tagged release workflow.
export const CURRENT_APP_VERSION = import.meta.env.VITE_APP_VERSION ?? PUBLISHED_WEB_VERSION;
export const LATEST_APK_URL = 'https://rs.vjelle.be/downloads/RSVjelle-v2.apk';
const VERSION_ENDPOINT = 'https://rs.vjelle.be/v1/app/version';

export interface AvailableAppUpdate {
  version: string;
  apkUrl: string;
}

export interface AppUpdateProgress { downloaded: number; total: number; percent: number }
export type AppUpdateStatus = 'permission_required' | 'installer_opened';

interface NativeAppUpdater {
  downloadAndInstall(options: { url: string }): Promise<{ status: AppUpdateStatus }>;
  installDownloaded(): Promise<{ status: AppUpdateStatus }>;
  addListener(eventName: 'downloadProgress', listener: (progress: AppUpdateProgress) => void): Promise<PluginListenerHandle>;
}

const AppUpdater = registerPlugin<NativeAppUpdater>('AppUpdater');

function isNativeApp(): boolean {
  return Boolean((window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());
}

function versionParts(version: string): number[] {
  return version.replace(/^v/i, '').split('.').map((part) => Number.parseInt(part, 10) || 0);
}

export function isNewerVersion(candidate: string, current: string): boolean {
  const candidateParts = versionParts(candidate);
  const currentParts = versionParts(current);
  for (let index = 0; index < Math.max(candidateParts.length, currentParts.length); index += 1) {
    const difference = (candidateParts[index] ?? 0) - (currentParts[index] ?? 0);
    if (difference !== 0) return difference > 0;
  }
  return false;
}

export async function checkForAppUpdate(): Promise<AvailableAppUpdate | null> {
  if (!isNativeApp()) return null;
  const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) return null;
  const candidate = await response.json() as Partial<AvailableAppUpdate>;
  if (!candidate.version || !candidate.apkUrl || !isNewerVersion(candidate.version, CURRENT_APP_VERSION)) return null;
  return { version: candidate.version, apkUrl: candidate.apkUrl };
}

export async function downloadAppUpdate(update: AvailableAppUpdate, onProgress: (progress: AppUpdateProgress) => void): Promise<AppUpdateStatus> {
  const listener = await AppUpdater.addListener('downloadProgress', onProgress);
  try {
    return (await AppUpdater.downloadAndInstall({ url: update.apkUrl })).status;
  } finally {
    await listener.remove();
  }
}

export async function installDownloadedUpdate(): Promise<AppUpdateStatus> {
  return (await AppUpdater.installDownloaded()).status;
}
